import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { config } from '../config';
import './LandingPage.css';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [showSignInForm, setShowSignInForm] = useState(false);
  const [signingIn, setSigningIn] = useState(false);

  useEffect(() => {
    // Check if we're returning from OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const company = urlParams.get('company');

    if (token && company === 'onboarding') {
      // This is an onboarding OAuth callback, redirect to onboarding with token
      navigate(`/onboarding?token=${token}&company=${company}`, { replace: true });
    } else if (token && company && company !== 'onboarding') {
      // This is a regular company login, redirect to admin dashboard
      localStorage.setItem('dashzap_token', token);
      navigate('/admin-dashboard', { replace: true });
    }
  }, [navigate]);

  const handleGetStarted = () => {
    navigate('/onboarding');
  };

  const handleSignInToDashboard = () => {
    console.log('[LandingPage] handleSignInToDashboard clicked');
    // Check if user has a valid admin token
    const adminToken = localStorage.getItem('dashzap_token');
    console.log('[LandingPage] adminToken from localStorage:', adminToken);

    if (adminToken) {
      try {
        // Decode JWT to check if it's valid and not for onboarding
        const payload = JSON.parse(atob(adminToken.split('.')[1]));
        console.log('[LandingPage] Decoded admin token payload:', payload);

        if (payload.companyId && payload.companyId !== 'onboarding') {
          // Valid admin token, go to dashboard
          console.log('[LandingPage] Valid admin token found, navigating to /admin-dashboard');
          navigate('/admin-dashboard');
          return;
        } else {
          console.log('[LandingPage] Admin token is for onboarding or missing companyId');
        }
      } catch (error) {
        // Invalid token, remove it
        console.log('[LandingPage] Error decoding admin token:', error);
        localStorage.removeItem('dashzap_token');
      }
    }

    // No valid admin token, show Google sign-in
    console.log('[LandingPage] No valid admin token, showing Google sign-in');
    setShowSignInForm(true);
  };

  const handleGoogleSignIn = () => {
    console.log('[LandingPage] Redirecting to server-side OAuth flow');
    setSigningIn(true);

    // Redirect to your server-side OAuth initiation
    window.location.href = `${config.API_BASE_URL}/auth/initiate`;
  };

  const handleCancelSignIn = () => {
    setShowSignInForm(false);
    setSigningIn(false);
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="logo">
            <h1>Dashzap</h1>
          </div>
          <nav className="nav">
            <a href="#features">Features</a>
            <a href="#pricing">Pricing</a>
            <a href="#contact">Contact</a>
            <button className="btn btn-login" onClick={handleSignInToDashboard}>
              Log In
            </button>
          </nav>
        </div>
      </header>

      {/* Email Migration Hero Section */}
      <section className="hero hero-primary">
        <div className="container">
          <div className="hero-content">
            <h2>Email Migration Services</h2>
            <p>
              Migrate from Gmail to Zoho Mail with zero downtime. Save 85% on email costs.
            </p>
            <div style={{
              background: '#f0fdf4',
              border: '2px solid #10b981',
              borderRadius: '12px',
              padding: '1.5rem',
              margin: '1.5rem 0',
              maxWidth: '400px'
            }}>
              <div style={{ fontSize: '0.9rem', color: '#065f46', marginBottom: '0.5rem' }}>
                Example: 20 Users
              </div>
              <div style={{ fontSize: '1.8rem', color: '#059669', fontWeight: 'bold' }}>
                Save ₹51,000/year
              </div>
              <div style={{ fontSize: '0.85rem', color: '#047857', marginTop: '0.5rem' }}>
                Pay only 3 months of savings
              </div>
            </div>
            <div className="hero-buttons">
              <button className="btn btn-primary" onClick={() => navigate('/migration-services')}>
                Get Your Savings Quote
              </button>
            </div>
          </div>
          <div className="hero-image">
            <div className="demo-card">
              <div className="demo-header">
                <div className="demo-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
              <div className="demo-content">
                <div className="migration-visual">
                  <div style={{ marginBottom: '1.5rem' }}>
                    <img
                      src="https://upload.wikimedia.org/wikipedia/commons/7/7e/Gmail_icon_%282020%29.svg"
                      alt="Gmail"
                      style={{ width: '80px', height: '80px' }}
                    />
                  </div>
                  <div style={{ fontSize: '3rem', margin: '1.5rem 0', color: '#667eea' }}>↓</div>
                  <div style={{ marginTop: '1.5rem' }}>
                    <img
                      src="/Zoho_Mail-256.png"
                      alt="Zoho Mail"
                      style={{ width: '80px', height: '80px', objectFit: 'contain' }}
                    />
                  </div>
                  <p className="migration-text" style={{ marginTop: '1.5rem', fontSize: '1.2rem', fontWeight: '600' }}>Gmail to Zoho</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Authentication Hero Section */}
      <section className="hero hero-secondary">
        <div className="container">
          <div className="hero-content">
            <h2>Secure Authentication as a Service</h2>
            <p>
              Streamline your app's authentication with our secure, scalable, and easy-to-integrate
              auth-as-a-service platform. Get your users logged in with Google OAuth in minutes.
            </p>
            <div className="hero-buttons">
              <button className="btn btn-primary" onClick={handleGetStarted}>
                Get Started Free
              </button>
            </div>
          </div>
          <div className="hero-image">
            <div className="demo-card">
              <div className="demo-header">
                <div className="demo-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
              <div className="demo-content">
                <div className="auth-form">
                  <h3>Sign in to Your App</h3>
                  <button className="google-btn">
                    <svg width="18" height="18" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continue with Google
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Email Sender (REST API) Hero Section */}
      <section className="hero hero-primary">
        <div className="container">
          <div className="hero-content">
            <h2>Email Sender via REST API</h2>
            <p>
              Send form submissions and transactional emails via simple REST API.
              No SMTP configuration needed - just integrate and send.
            </p>
            <div className="hero-buttons">
              <button className="btn btn-primary" onClick={handleGetStarted}>
                Get Started Free
              </button>
            </div>
          </div>
          <div className="hero-image">
            <div className="demo-card">
              <div className="demo-header">
                <div className="demo-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
              <div className="demo-content">
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📨</div>
                  <div style={{
                    background: '#f3f4f6',
                    padding: '1rem',
                    borderRadius: '8px',
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                    textAlign: 'left',
                    marginTop: '1rem'
                  }}>
                    <div>POST /mail-handler/send</div>
                    <div style={{ color: '#6b7280', marginTop: '0.5rem' }}>{"{"}</div>
                    <div style={{ color: '#6b7280', marginLeft: '1rem' }}>"formId": "contact"</div>
                    <div style={{ color: '#6b7280', marginLeft: '1rem' }}>"email": "..."</div>
                    <div style={{ color: '#6b7280' }}>{"}"}</div>
                  </div>
                  <div style={{ marginTop: '1rem', color: '#059669', fontWeight: 'bold' }}>
                    ✓ 500 emails/day per form
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Email Backup Hero Section */}
      <section className="hero hero-secondary">
        <div className="container">
          <div className="hero-content">
            <h2>Periodic Email Backup to S3</h2>
            <p>
              Automatically backup your Gmail/POP3 emails to secure AWS S3 storage.
              Download as MBOX or archive format anytime.
            </p>
            <div className="hero-buttons">
              <button className="btn btn-primary" onClick={handleGetStarted}>
                Start Backing Up
              </button>
            </div>
          </div>
          <div className="hero-image">
            <div className="demo-card">
              <div className="demo-header">
                <div className="demo-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
              <div className="demo-content">
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🔒</div>
                  <div style={{ fontSize: '2.5rem', margin: '1rem 0', color: '#667eea' }}>↓</div>
                  <div style={{ fontSize: '4rem' }}>☁️</div>
                  <p style={{ marginTop: '1.5rem', fontSize: '1.1rem', fontWeight: '600', color: '#4b5563' }}>
                    Email → AWS S3
                  </p>
                  <div style={{ marginTop: '1rem', color: '#059669', fontSize: '0.9rem' }}>
                    ✓ MBOX Format<br/>
                    ✓ Archive Format<br/>
                    ✓ Secure & Encrypted
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Hero Section */}
      <section className="hero hero-primary">
        <div className="container">
          <div className="hero-content">
            <h2>Newsletter as a Service</h2>
            <p>
              Create, manage, and send beautiful newsletters to your subscribers.
              Track opens, clicks, and engagement effortlessly.
            </p>
            <div className="hero-buttons">
              <button className="btn btn-primary" onClick={handleGetStarted}>
                Create Newsletter
              </button>
            </div>
          </div>
          <div className="hero-image">
            <div className="demo-card">
              <div className="demo-header">
                <div className="demo-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
              <div className="demo-content">
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📰</div>
                  <div style={{
                    background: '#f3f4f6',
                    padding: '1.5rem',
                    borderRadius: '8px',
                    marginTop: '1rem'
                  }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '1rem', color: '#111827' }}>
                      Your Newsletter
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280', textAlign: 'left' }}>
                      <div>✓ Drag & Drop Editor</div>
                      <div style={{ marginTop: '0.5rem' }}>✓ Template Library</div>
                      <div style={{ marginTop: '0.5rem' }}>✓ Analytics Dashboard</div>
                      <div style={{ marginTop: '0.5rem' }}>✓ Subscriber Management</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sign In Modal */}
      {showSignInForm && (
        <div className="modal-overlay" onClick={handleCancelSignIn}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Sign In to Dashboard</h3>
              <button className="modal-close" onClick={handleCancelSignIn}>×</button>
            </div>
            <div className="signin-form">
              <div className="form-group">
                <p>Sign in with your Google account to access your dashboard</p>
                <button
                  className="btn btn-primary"
                  onClick={handleGoogleSignIn}
                  disabled={signingIn}
                  style={{ width: '100%', marginBottom: '1rem' }}
                >
                  {signingIn ? 'Signing you in...' : 'Continue with Google'}
                </button>
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-outline" onClick={handleCancelSignIn} disabled={signingIn}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <h2>Ready to Get Started?</h2>
          <p>Complete email solutions - from migration to sending, backup to newsletters.</p>
          <button className="btn btn-primary btn-large" onClick={handleGetStarted}>
            Start Your Free Account
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h4>Dashzap</h4>
              <p>Your complete email services platform.</p>
            </div>
            <div className="footer-section">
              <h4>Product</h4>
              <ul>
                <li><a href="#features">Features</a></li>
                <li><a href="#pricing">Pricing</a></li>
                <li><a href="#">Documentation</a></li>
                <li><a href="#">API Reference</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Company</h4>
              <ul>
                <li><a href="#">About</a></li>
                <li><a href="#">Blog</a></li>
                <li><a href="#contact">Contact</a></li>
                <li><a href="#">Status</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Support</h4>
              <ul>
                <li><a href="#">Help Center</a></li>
                <li><a href="#">Community</a></li>
                <li><a href="#">Contact Support</a></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2024 Dashzap. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;